import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DepartmentLabels } from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { notifyClaimChanged } from './useDesk.js';

/**
 * As etiquetas no card do sinistro (07/10/2026): o que o seletor oferece e
 * gravar a escolha.
 *
 * O catálogo é de cada SETOR (Plataforma › Departamentos); cada pessoa vê e
 * põe as dos setores dela. A raiz é a mesma de `useClaims.ts` e `useDesk.ts`
 * (`['sinistros', 'sinistro']`): gravar muda a ficha, a lista e a Atividade
 * de uma vez.
 */

const ROOT = ['sinistros', 'sinistro'] as const;

export function useLabelPermissions() {
  const { data: context } = useSessionContext();
  return {
    canApply: can(context, 'claims.labels', 'aplicar'),
  };
}

/** As etiquetas ativas de cada setor de quem usa. */
export function useLabelOptions() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  return useQuery({
    queryKey: [...ROOT, 'etiquetas', tenantId],
    queryFn: async (): Promise<DepartmentLabels[]> => {
      const r = await api.claims.listLabelOptions();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as etiquetas'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** Grava as etiquetas do card que são dos setores de quem grava. O quadro acompanha. */
export function useSetClaimLabels(claimId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (labelIds: number[]) =>
      ok(
        await api.claims.setClaimLabels({ params: { id: claimId }, body: { labelIds } }),
        200,
        'Não foi possível gravar as etiquetas.',
      ),
    onSettled: () => {
      notifyClaimChanged(claimId);
      return client.invalidateQueries({ queryKey: [...ROOT] });
    },
  });
}
